import { useMutation, useQuery } from "@tanstack/react-query";

import {
  GeneratePreDefinedAutomationsUrl,
  GetAllAutomationsUrl,
  GetAutomationByIdUrl,
  GetListingManagmentUrl,
  GetMasterDataUrl,
  GetOnboardingUrl,
  SetOnboardingCompleteUrl,
  updateActivityUrl,
  getCheckOnboardingUrl,
  GetUnSyncedPropertiesUrl,
  SyncPropertyUrl,
} from "../utils/constants";
import { useApi } from "./useApi";

export type errorTypeWebsiteBuilder = {
  response: {
    data: { errorMessages: { messageAr: string; messageEn: string }[] };
  };
};

//Api Hooks
const useAutomation = () => {
  const { apiPrivate } = useApi();

  const GetOnboardingApi = async () => {
    const { data } = await apiPrivate.get(`${GetOnboardingUrl}`);
    return data;
  };

  const GeneratePreDefinedAutomationsApi = async (body: any) => {
    const { data } = await apiPrivate.post(
      `${GeneratePreDefinedAutomationsUrl}`,
      body
    );
    return data;
  };

  const GetAllAutomationsApi = async (params: {
    pageSize: any;
    pageNumber: any;
  }) => {
    const { data } = await apiPrivate.get(
      `${GetAllAutomationsUrl}?pageSize=${params.pageSize}&pageNumber=${params.pageNumber}`
    );
    return data;
  };

  const GetListingManagmentApi = async ({
    pageSize = 10,
    pageNumber = 1,
  }: { pageSize?: any; pageNumber?: any } = {}) => {
    const { data } = await apiPrivate.get(
      `${GetListingManagmentUrl}?pageSize=${pageSize}&pageNumber=${pageNumber}`
    );
    return data;
  };
  const updateActivityApi = async ({ id }: any) => {
    const { data } = await apiPrivate.post(`${updateActivityUrl}?Id=${id}`);
    return data;
  };

  const SetOnboardingCompleteApi = async ({ id }: any) => {
    const { data } = await apiPrivate.post(
      `${SetOnboardingCompleteUrl}?Id=${id}`
    );
    return data;
  };

  const GetAutomationByIdApi = async ({ id }: any) => {
    const { data } = await apiPrivate.get(`${GetAutomationByIdUrl}?Id=${id}`);
    return data;
  };

  const GetMasterDataApi = async () => {
    const { data } = await apiPrivate.get(`${GetMasterDataUrl}`);
    return data;
  };

  const GetUnSyncedPropertiesApi = async () => {
    const { data } = await apiPrivate.get(`${GetUnSyncedPropertiesUrl}`);
    return data;
  };

  const SyncPropertyApi = async (id = []) => {
    const { data } = await apiPrivate.patch(`${SyncPropertyUrl}`, id, {
      headers: {
        accept: "text/plain",
        "Content-Type": "application/json-patch+json",
      },
    });
    return data;
  };

  // Query

  const GetOnboardingQuery = () =>
    useQuery({
      enabled: false,
      queryKey: [GetOnboardingUrl],
      queryFn: ({ queryKey }) => GetOnboardingApi(),
    });

  const GetAllAutomationsQuery = (data: {
    pageSize: string;
    pageNumber: string;
    search?: string;
  }) =>
    useQuery({
      enabled: true,
      queryKey: [GetAllAutomationsUrl, data],
      queryFn: ({ queryKey }) => GetAllAutomationsApi(data),
    });

  const GetListingManagmentQuery = (data?: {
    pageSize?: any;
    pageNumber?: any;
  }) =>
    useQuery({
      enabled: true,
      queryKey: [
        GetListingManagmentUrl,
        data?.pageNumber || 1,
        data?.pageSize || 10,
      ],
      queryFn: ({ queryKey }) => GetListingManagmentApi(data || {}),
    });

  const GetAutomationByIdQuery = (data: { id: any }) =>
    useQuery({
      enabled: true,
      queryKey: [GetAutomationByIdUrl, data?.id],
      queryFn: ({ queryKey }) => GetAutomationByIdApi(data),
    });

  const GetMasterDataQuery = () =>
    useQuery({
      enabled: true,
      queryKey: [GetMasterDataUrl],
      queryFn: ({ queryKey }) => GetMasterDataApi(),
    });

  const GetUnSyncedPropertiesQuery = () =>
    useQuery({
      enabled: true,
      queryKey: [GetUnSyncedPropertiesUrl],
      queryFn: ({ queryKey }) => GetUnSyncedPropertiesApi(),
    });
  // Mutate

  const GeneratePreDefinedAutomationsMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: errorTypeWebsiteBuilder) => void;
  }) =>
    useMutation({
      mutationFn: (body: any) => GeneratePreDefinedAutomationsApi(body),
      onSuccess: (data) => onSuccess && onSuccess(),
      onError: (error: errorTypeWebsiteBuilder) => onError && onError(error),
    });

  const updateActivityMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: errorTypeWebsiteBuilder) => void;
  }) =>
    useMutation({
      mutationFn: (body: any) => updateActivityApi(body),
      onSuccess: (data) => onSuccess && onSuccess(),
      onError: (error: errorTypeWebsiteBuilder) => onError && onError(error),
    });

  const SetOnboardingCompleteMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: errorTypeWebsiteBuilder) => void;
  }) =>
    useMutation({
      mutationFn: (body: any) => SetOnboardingCompleteApi(body),
      onSuccess: (data) => onSuccess && onSuccess(),
      onError: (error: errorTypeWebsiteBuilder) => onError && onError(error),
    });

  const SyncPropertyMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: errorTypeWebsiteBuilder) => void;
  }) =>
    useMutation({
      mutationFn: (id: any) => SyncPropertyApi(id),
      onSuccess: (data) => onSuccess && onSuccess(),
      onError: (error: errorTypeWebsiteBuilder) => onError && onError(error),
    });

  return {
    GetOnboardingQuery,
    GeneratePreDefinedAutomationsMutate,
    GetAllAutomationsQuery,
    GetListingManagmentQuery,
    updateActivityMutate,
    SetOnboardingCompleteMutate,
    GetAutomationByIdQuery,
    GetMasterDataQuery,
    GetUnSyncedPropertiesQuery,
    SyncPropertyMutate,
  };
};

export { useAutomation };
